import React, { useState } from 'react';
import { FaEdit, FaTrash, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { useWatchlist } from '../context/WatchlistContext';
import './WatchlistPage.css';

const WatchlistPage = ({ onBack }) => {
  const { 
    watchlists, 
    activeWatchlistId, 
    setActiveWatchlistId,
    createWatchlist,
    renameWatchlist,
    deleteWatchlist,
    removeFromWatchlist
  } = useWatchlist();

  const [editingWatchlistId, setEditingWatchlistId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');

  const activeWatchlist = watchlists.find(wl => wl.id === activeWatchlistId) || watchlists[0];
  const watchlistData = activeWatchlist?.stocks || [];

  const handleStartEdit = (watchlist) => {
    setEditingWatchlistId(watchlist.id);
    setEditingName(watchlist.name);
  };

  const handleSaveEdit = () => {
    if (editingName.trim()) {
      renameWatchlist(editingWatchlistId, editingName.trim());
    }
    setEditingWatchlistId(null);
    setEditingName('');
  };

  const handleCancelEdit = () => {
    setEditingWatchlistId(null);
    setEditingName('');
  };

  const handleCreateNew = () => {
    if (newWatchlistName.trim()) {
      const newId = createWatchlist(newWatchlistName.trim());
      setActiveWatchlistId(newId);
      setNewWatchlistName('');
      setShowCreateNew(false);
    }
  };

  const handleDeleteWatchlist = (id) => {
    if (window.confirm('Are you sure you want to delete this watchlist?')) {
      deleteWatchlist(id);
    }
  };

  const handleRemoveStock = (symbol) => {
    if (window.confirm('Remove this stock from watchlist?')) {
      removeFromWatchlist(symbol, activeWatchlistId);
    }
  };

  const calculateStats = () => {
    const gainers = watchlistData.filter(s => (s.change || s.percentage_change || 0) > 0).length;
    const losers = watchlistData.filter(s => (s.change || s.percentage_change || 0) < 0).length;
    const total = watchlistData.length;
    const avgChange = total > 0 
      ? watchlistData.reduce((sum, s) => sum + (s.changePercent || s.percentage_change || 0), 0) / total 
      : 0;
    
    return { gainers, losers, total, avgChange };
  };

  const stats = calculateStats();

  return (
    <div className="watchlist-page">
      <header className="watchlist-header">
        <div className="logo">
          <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
        </div>
      </header>

      <main className="watchlist-main">
        <div className="watchlist-container">
          <div className="watchlist-title s4u-title-row">
            <div className="s4u-page-heading">
              <button className="watchlist-back-btn s4u-back-btn" onClick={onBack}>
                ← Back to Dashboard
              </button>
              <h1>My Watchlists</h1>
            </div>
            <div className="watchlist-info">
              <span className="info-icon">⭐</span>
              <span className="info-text">Track your favorite stocks</span>
            </div>
          </div>

          <div className="watchlist-tabs-container">
            <div className="watchlist-tabs">
              {watchlists.map(wl => (
                <div
                  key={wl.id}
                  className={`watchlist-tab ${wl.id === activeWatchlistId ? 'active' : ''}`}
                  onClick={() => setActiveWatchlistId(wl.id)}
                >
                  {editingWatchlistId === wl.id ? (
                    <div className="tab-edit-mode" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit();
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                        autoFocus
                        className="tab-edit-input"
                      />
                      <button onClick={handleSaveEdit} className="tab-btn save">
                        <FaCheck />
                      </button>
                      <button onClick={handleCancelEdit} className="tab-btn cancel">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="tab-name">{wl.name}</span>
                      <span className="tab-count">{wl.stocks.length}</span>
                      <div className="tab-actions">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit(wl);
                          }} 
                          className="tab-action-btn"
                          title="Rename watchlist"
                        >
                          <FaEdit />
                        </button>
                        {watchlists.length > 1 && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteWatchlist(wl.id);
                            }} 
                            className="tab-action-btn delete"
                            title="Delete watchlist"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
              
              {showCreateNew ? (
                <div className="watchlist-tab create-mode">
                  <input
                    type="text"
                    placeholder="Watchlist name..."
                    value={newWatchlistName}
                    onChange={(e) => setNewWatchlistName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateNew();
                      if (e.key === 'Escape') {
                        setShowCreateNew(false);
                        setNewWatchlistName('');
                      }
                    }}
                    autoFocus
                    className="tab-create-input"
                  />
                  <button onClick={handleCreateNew} className="tab-btn save">
                    <FaCheck />
                  </button>
                  <button 
                    onClick={() => {
                      setShowCreateNew(false);
                      setNewWatchlistName('');
                    }} 
                    className="tab-btn cancel"
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <button 
                  className="watchlist-tab add-new"
                  onClick={() => setShowCreateNew(true)}
                  title="Create new watchlist"
                >
                  <FaPlus /> New Watchlist
                </button>
              )}
            </div>
          </div>

          <div className="watchlist-summary">
            <div className="summary-card">
              <h3>{activeWatchlist.name} Summary</h3>
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-icon">📈</span>
                  <span className="stat-label">Gainers</span>
                  <span className="stat-value positive">{stats.gainers}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">📉</span>
                  <span className="stat-label">Losers</span>
                  <span className="stat-value negative">{stats.losers}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">🎯</span>
                  <span className="stat-label">Total Stocks</span>
                  <span className="stat-value">{stats.total}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">📊</span>
                  <span className="stat-label">Avg Change</span>
                  <span className={`stat-value ${stats.avgChange >= 0 ? 'positive' : 'negative'}`}>
                    {stats.avgChange >= 0 ? '+' : ''}{stats.avgChange.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="watchlist-table-container">
            <div className="table-header-info">
              <span className="results-count">Showing {watchlistData.length} stocks</span>
              <span className="last-updated">Last updated: Real-time</span>
            </div>

            {watchlistData.length === 0 ? (
              <div className="empty-watchlist">
                <span className="empty-icon">📊</span>
                <h3>No stocks in this watchlist</h3>
                <p>Click the star icon on any stock to add it here</p>
              </div>
            ) : (
              <div className="watchlist-table">
                <div className="table-row--watchlist table-header-row">
                  <div className="table-cell">Symbol</div>
                  <div className="table-cell">Name</div>
                  <div className="table-cell">LTP</div>
                  <div className="table-cell">Change</div>
                  <div className="table-cell">Change %</div>
                  <div className="table-cell">Actions</div>
                </div>

                {watchlistData.map((stock, index) => {
                  const change = stock.change || stock.percentage_change || 0;
                  const changePercent = stock.changePercent || stock.percentage_change || 0;
                  const ltp = stock.ltp || 0;
                  const isPositive = change >= 0;
                  
                  return (
                    <div key={stock.symbol || index} className="table-row--watchlist stock-row">
                      <div className="table-cell stock-symbol">{stock.symbol}</div>
                      <div className="table-cell stock-name">{stock.name || stock.symbol}</div>
                      <div className="table-cell stock-price">
                        {ltp ? `₹${ltp.toFixed(2)}` : '—'}
                      </div>
                      <div className={`table-cell stock-change ${isPositive ? 'positive' : 'negative'}`}>
                        {change ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}` : '—'}
                      </div>
                      <div className={`table-cell stock-change-percent ${isPositive ? 'positive' : 'negative'}`}>
                        {changePercent ? `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%` : '—'}
                      </div>
                      <div className="table-cell stock-actions">
                        <button 
                          className="action-btn remove"
                          onClick={() => handleRemoveStock(stock.symbol)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default WatchlistPage;